import { supabase } from '@/lib/supabase/client';
import type { CustomerAccount, CustomerAccountInsert } from '@/types/customer-accounts';
import type { Location } from '@/types/location';

const CUSTOMER_ACCOUNTS_MIGRATION_ERROR =
  'customer_accounts is not available in this environment yet. Run migration 078_customer_accounts_and_invoice_provisioning.sql and try again.';

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

function isMissingCustomerAccountsColumn(message: string | undefined): boolean {
  if (!message) return false;
  return message.includes('customer_accounts') || message.includes('customer_account_id');
}

function slugAccountKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const customerAccountsService = {
  async getAll(): Promise<CustomerAccount[]> {
    const { data, error } = await supabase
      .from('customer_accounts')
      .select('*')
      .order('account_label', { ascending: true });

    if (error) {
      if (isMissingCustomerAccountsColumn(error.message)) {
        return [];
      }
      throw new Error(`Failed to fetch customer accounts: ${error.message}`);
    }

    return (data ?? []) as CustomerAccount[];
  },

  async getByAccountKey(accountKey: string): Promise<CustomerAccount | null> {
    const { data, error } = await supabase
      .from('customer_accounts')
      .select('*')
      .eq('account_key', accountKey)
      .maybeSingle();

    if (error) {
      if (isMissingCustomerAccountsColumn(error.message)) {
        return null;
      }
      throw new Error(`Failed to fetch customer account: ${error.message}`);
    }

    return (data as CustomerAccount | null) ?? null;
  },

  async create(data: CustomerAccountInsert): Promise<CustomerAccount> {
    const userId = await getCurrentUserId();
    const { data: created, error } = await supabase
      .from('customer_accounts')
      .insert({
        account_key: data.account_key,
        account_label: data.account_label,
        legal_entity_name: data.legal_entity_name ?? null,
        primary_location_id: data.primary_location_id ?? null,
        created_by: userId,
        updated_by: userId,
      })
      .select()
      .single();

    if (error) {
      if (isMissingCustomerAccountsColumn(error.message)) {
        throw new Error(CUSTOMER_ACCOUNTS_MIGRATION_ERROR);
      }
      throw new Error(`Failed to create customer account: ${error.message}`);
    }

    return created as CustomerAccount;
  },

  /** Auto 1:1 account for manual location create when no portfolio account is chosen. */
  async ensureSingleStoreAccountForLocation(location: Location): Promise<CustomerAccount | null> {
    if (location.customer_account_id) {
      return null;
    }

    const accountKey =
      slugAccountKey(location.location_number) || slugAccountKey(location.location_name);
    if (!accountKey) {
      return null;
    }

    const existing = await this.getByAccountKey(accountKey);
    const account =
      existing ??
      (await this.create({
        account_key: accountKey,
        account_label: location.location_name,
        primary_location_id: location.id,
      }));

    const { error } = await supabase
      .from('locations')
      .update({
        customer_account_id: account.id,
        updated_by: await getCurrentUserId(),
      })
      .eq('id', location.id)
      .is('customer_account_id', null);

    if (error) {
      if (isMissingCustomerAccountsColumn(error.message)) {
        return null;
      }
      throw new Error(`Failed to link location to customer account: ${error.message}`);
    }

    if (!account.primary_location_id) {
      await supabase
        .from('customer_accounts')
        .update({ primary_location_id: location.id })
        .eq('id', account.id)
        .is('primary_location_id', null);
    }

    return account;
  },
};
